import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaBuilding } from 'react-icons/fa';
import ContactForm from './ContactForm';
import Map from './Map';

const ContactSection = styled.section`
  background-color: ${props => props.theme.colors.offWhite};
`;

const SectionContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.darkGray};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 3px;
    background-color: ${props => props.theme.colors.accent};
  }
`;

const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfoContainer = styled(motion.div)`
  background-color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: ${props => props.theme.spacing.lg};
  box-shadow: ${props => props.theme.shadows.medium};
  display: flex;
  flex-direction: column;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    order: 2;
  }
`;

const ContactInfoTitle = styled.h3`
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.darkGray};
`;

const ContactInfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ContactInfoItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.gray};
`;

const ContactIcon = styled(motion.div)`
  width: 50px;
  height: 50px;
  background-color: ${props => props.theme.colors.darkGray};
  color: ${props => props.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-right: ${props => props.theme.spacing.md};
  font-size: 20px;
  transition: all 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  svg {
    width: 24px;
    height: 24px;
  }
  
  &:hover {
    transform: scale(1.1);
  }
`;

const ContactText = styled.div`
  flex: 1;
  
  h4 {
    margin-bottom: ${props => props.theme.spacing.xs};
    color: ${props => props.theme.colors.darkGray};
  }
  
  p {
    margin: 0;
  }
`;

const MapContainer = styled(motion.div)`
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.medium};
  height: 300px;
  margin-top: auto;
`;

const FormContainer = styled(motion.div)`
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    order: 1;
  }
`;

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const iconVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { 
        type: "spring",
        stiffness: 300,
        damping: 10,
        duration: 0.6
      }
    },
    hover: { 
      scale: 1.1,
      rotate: [0, 10, -10, 0],
      transition: { 
        scale: {
          type: "spring",
          stiffness: 300,
          damping: 10
        },
        rotate: {
          type: "tween",
          duration: 0.5,
          ease: "easeInOut"
        }
      }
    }
  };
  
  return (
    <ContactSection id="contact" ref={ref}>
      <SectionContainer>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.7 }}
        >
          Contact AI AUTOMATION
        </SectionTitle>
        
        <ContactContent>
          <FormContainer
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <ContactForm />
          </FormContainer>
          
          <ContactInfoContainer
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            <ContactInfoTitle>Get In Touch</ContactInfoTitle>
            
            <ContactInfoList>
              <ContactInfoItem>
                <ContactIcon
                  initial="hidden"
                  animate="visible"
                  whileHover="hover"
                  variants={iconVariants}
                >
                  <FaMapMarkerAlt />
                </ContactIcon>
                <ContactText>
                  <h4>Address</h4>
                  <p>Aludarių g. 5-66, </p>
                  <p>LT-01113 Vilnius, Lithuania</p>
                </ContactText>
              </ContactInfoItem>
              
              <ContactInfoItem>
                <ContactIcon
                  initial="hidden"
                  animate="visible"
                  whileHover="hover"
                  variants={iconVariants}
                >
                  <FaPhone />
                </ContactIcon>
                <ContactText>
                  <h4>Phone</h4>
                  <p>+370 (614) 69 060</p>
                </ContactText>
              </ContactInfoItem>
              
              <ContactInfoItem>
                <ContactIcon
                  initial="hidden"
                  animate="visible"
                  whileHover="hover"
                  variants={iconVariants}
                >
                  <FaEnvelope />
                </ContactIcon>
                <ContactText>
                  <h4>Email</h4>
                  <p>contactaiautomation@gmail.com</p>
                </ContactText>
              </ContactInfoItem>
              
              <ContactInfoItem>
                <ContactIcon
                  initial="hidden"
                  animate="visible"
                  whileHover="hover"
                  variants={iconVariants}
                >
                  <FaBuilding />
                </ContactIcon>
                <ContactText>
                  <h4>Registration Details</h4>
                  <p>MB 306445323</p>
                  <p>VAT: LT100017727618</p>
                  <p>Reg. Address: Aludarių g. 5-66, LT-01113 Vilnius</p>
                </ContactText>
              </ContactInfoItem>
            </ContactInfoList>
            
            <MapContainer
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.7, delay: 0.6 }}
            >
              <Map />
            </MapContainer>
          </ContactInfoContainer>
        </ContactContent>
      </SectionContainer>
    </ContactSection>
  );
};

export default Contact; 